import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS } from 'constants/featureFlags';
import { formatDateWithTime } from 'helpers/intl-helper';
import {
  exportStates,
  reorderTypes,
} from 'components/projects/taskList/constants';
import { graphQLIdFromProjectDBID } from 'helpers/graphql-utils';
import { setTaskListReorderingType } from 'actions/todos';
import { useCurrentUser } from 'javascripts/hooks';
import { STANDARD, userRoleIsAtLeast } from 'helpers/roles';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import Assign from 'components/projects/taskList/assign/Assign';
import ExportTaskList from 'components/projects/taskList/ExportTaskList';
import TaskListDeleteModal from 'components/projects/taskList/TaskListDeleteModal';
import TaskListFormModal from 'components/projects/taskList/TaskListFormModal';
import { ProgressCircle } from '@companycam/slab-web';
import TaskListHeaderMenu from 'components/projects/taskList/components/TaskListHeaderMenu';
import useChecklistAdminStatus from 'components/projects/taskList/hooks/useChecklistAdminStatus';
import useTaskListCounts from 'components/projects/taskList/hooks/useTaskListCounts';
import { translate } from 'translations/i18n';
import BackwardsCompatibleLink from 'components/navigation/BackwardsCompatibleLink';
import { BackIcon } from 'components/templates/shared/styledComponents';

// #region styled-components
const TaskListHeaderContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  padding-bottom: var(--cc_size_spacing_l);
  margin-bottom: var(--cc_size_spacing_l);
  border-bottom: 4px solid var(--cc_color_background_3);
`;

const TaskListHeadingContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0 16px;
`;

const TaskListHeading = styled.h1`
  flex: 1;
  padding-right: 32px;
  margin: 0;
  font-weight: 800;
  line-height: 1.25;
  white-space: pre-wrap;
  margin-top: ${(props) => (props.template ? 'var(--cc_size_spacing_l)' : '0')};
  overflow-wrap: anywhere;
`;

const TaskListMetaContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: var(--cc_size_spacing_xs);
`;

const TextMeta = styled(Text)`
  &::before {
    content: '•';
    margin: 0 var(--cc_size_spacing_s);
  }
`;

const BreadcrumbContainer = styled.div`
  margin-top: -16px;
  margin-bottom: 16px;
`;

const TemplateTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const TemplateDescription = styled.p`
  width: 100%;
  margin: 0 0 var(--cc_size_spacing_s);
  line-height: 1.5;
  white-space: pre-wrap;
`;
// #endregion

const TaskListHeader = ({
  template,
  taskList,
  projectId,
  showSectionsRearrangeOption,
}) => {
  const dispatch = useDispatch();

  const flags = useFlags();
  const skipSectionsEnabled = flags[LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS];

  const { projectId: templateProjectId } = queryString.parse(
    window.location.search,
  );
  const [showTaskListFormModal, setShowTaskListFormModal] = useState(false);
  const [showTaskListDeleteModal, setShowTaskListDeleteModal] = useState(false);
  const [exportState, setExportState] = useState(exportStates.NONE);
  const [createTemplate, setCreateTemplate] = useState(false);

  const projectNodeId = graphQLIdFromProjectDBID(projectId);
  const user = useCurrentUser();
  const hasUserRoleStandardAccess = userRoleIsAtLeast(user.user_role, STANDARD);
  const taskCount = template
    ? template.taskTemplatesCount
    : taskList.tasksCount;

  const { isChecklistAdmin, isTemplateAdmin } = useChecklistAdminStatus({
    checklistCreatorId: taskList?.creator?.id,
    projectNodeId,
  });

  const { tasksCompletedCount, tasksTotalCount, percentageCompleted } =
    useTaskListCounts({ taskList: taskList ?? template });

  const renderHeader = () => {
    if (template) {
      return (
        <>
          <TemplateTitleContainer>
            <TaskListHeading template={template}>
              {template.name}
            </TaskListHeading>
          </TemplateTitleContainer>
          <TemplateDescription>{template.description}</TemplateDescription>
        </>
      );
    }

    if (!taskList) {
      return null;
    }

    // Note: A task list with zero items is considered incomplete.
    const isTaskListCompleted = tasksTotalCount
      ? tasksCompletedCount === tasksTotalCount
      : false;

    return (
      <TaskListHeadingContainer>
        <ProgressCircle
          color={isTaskListCompleted ? 'success' : 'secondary'}
          size="large"
          id="progress"
          label={translate('common.percentComplete')}
          max={100}
          value={percentageCompleted}
          shouldShowCompleteIcon={isTaskListCompleted}
          data-testid={
            isTaskListCompleted
              ? 'project__checklists__header__completed_icon'
              : ''
          }
          formatValue={({ value, max }) =>
            `${Math.round((value / max) * 100)}%`
          }
        />
        <div>
          <TaskListHeading>{taskList.name}</TaskListHeading>
          <TaskListMetaContainer>
            <Text color="subtle" size="s" weight="500">
              {`${tasksCompletedCount}/${tasksTotalCount} ${translate(
                'projects.taskList.taskListHeader.tasksCompleted',
              )}`}
            </Text>

            {taskList.tasksSkippedCount > 0 && skipSectionsEnabled ? (
              <Text color="subtle" size="s" weight="500">
                , {taskList.tasksSkippedCount} N/A
              </Text>
            ) : null}

            <TextMeta color="nonessential" size="xs">
              {translate('common.lastUpdated')}{' '}
              {formatDateWithTime(taskList.updatedAt)}
            </TextMeta>
          </TaskListMetaContainer>
          {hasUserRoleStandardAccess && !template ? (
            <Assign taskListId={taskList?.id} />
          ) : null}
        </div>
      </TaskListHeadingContainer>
    );
  };

  const renderHeaderNavigation = () => {
    if (template) {
      return templateProjectId ? (
        <BreadcrumbContainer>
          <BackwardsCompatibleLink
            variant="breadcrumb"
            to={`/projects/${templateProjectId}/todos`}
          >
            <BackIcon />
            {translate('projects.taskList.taskListHeader.backToTaskLists')}
          </BackwardsCompatibleLink>
        </BreadcrumbContainer>
      ) : null;
    } else {
      return (
        <BreadcrumbContainer>
          <BackwardsCompatibleLink
            variant="breadcrumb"
            to={`/projects/${projectId}/todos`}
          >
            <BackIcon />
            {translate('projects.taskList.taskListHeader.backToTaskLists')}
          </BackwardsCompatibleLink>
        </BreadcrumbContainer>
      );
    }
  };

  return (
    <>
      {renderHeaderNavigation()}
      <TaskListHeaderContainer template={template}>
        {renderHeader()}
        <TaskListHeaderMenu
          isVisible={!template}
          showEditOptions={isChecklistAdmin}
          showReorderSectionsOption={
            showSectionsRearrangeOption ? isChecklistAdmin : false
          }
          showReorderTasksOption={taskCount > 1 && isChecklistAdmin}
          showTemplateOption={isTemplateAdmin}
          showDeleteOption={isChecklistAdmin}
          onEditClick={() => setShowTaskListFormModal(true)}
          onExportClick={() => setExportState(exportStates.EXPORT)}
          onReorderSectionsClick={() => {
            dispatch(setTaskListReorderingType(reorderTypes.SECTIONS));
          }}
          onReorderTasksClick={() => {
            dispatch(setTaskListReorderingType(reorderTypes.TASKS));
          }}
          onSaveAsTemplateClick={() => {
            setCreateTemplate(true);
            setShowTaskListFormModal(true);
          }}
          onDeleteClick={() => setShowTaskListDeleteModal(true)}
          taskListName={taskList?.name}
        />
      </TaskListHeaderContainer>
      <TaskListFormModal
        templateCreateFromTaskList={createTemplate}
        projectNodeId={projectNodeId}
        isOpen={showTaskListFormModal}
        taskList={taskList}
        onRequestClose={() => {
          setCreateTemplate(false);
          setShowTaskListFormModal(false);
        }}
      />
      <TaskListDeleteModal
        id={projectId}
        taskListDeleteId={taskList ? taskList.id : ''}
        isOpen={showTaskListDeleteModal}
        projectNodeId={projectNodeId}
        onRequestClose={() => setShowTaskListDeleteModal(false)}
      />
      {taskList ? (
        <ExportTaskList
          taskList={taskList}
          exportState={exportState}
          setExportState={setExportState}
        />
      ) : null}
    </>
  );
};

TaskListHeader.propTypes = {
  taskList: PropTypes.object,
  projectId: PropTypes.string,
  showSectionsRearrangeOption: PropTypes.bool,
  template: PropTypes.shape({
    taskTemplatesCount: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
};

export default TaskListHeader;
